import { useState } from 'react'
import { ActionIcon, Button, List, Modal, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconDownload } from '@tabler/icons-react'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import { isStandalonePWA } from '@/utils/pwa'

/**
 * Botão "Instalar app".
 *
 * Dois caminhos:
 * 1. **Nativo** — quando o Chromium dispara `beforeinstallprompt` (PWA instalável e
 *    ainda não instalado), o clique abre o diálogo de instalação nativo.
 * 2. **Fallback (mobile)** — quando o evento NÃO dispara (Chrome faz throttle após
 *    desinstalar, ou iOS Safari que nunca dispara), o botão continua visível no
 *    celular e abre um modal com instruções de instalação manual. Garante que o
 *    ícone sempre apareça no celular (exceto quando já rodando como app instalado).
 *
 * `compact` renderiza só o ícone (para caber no header mobile).
 */
export function InstallAppButton({ compact = false }: { compact?: boolean }) {
  const { canInstall, promptInstall } = useInstallPrompt()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [helpOpen, setHelpOpen] = useState(false)

  // Já rodando como app instalado → nada a oferecer.
  if (isStandalonePWA()) return null

  // Sem prompt nativo e não é mobile → não há caminho útil (desktop sem evento).
  if (!canInstall && !isMobile) return null

  const onClick = () => {
    if (canInstall) {
      void promptInstall()
    } else {
      setHelpOpen(true)
    }
  }

  const trigger = compact ? (
    <ActionIcon
      variant="light"
      color="inovareTeal"
      size="lg"
      onClick={onClick}
      aria-label="Instalar app"
      title="Instalar app"
    >
      <IconDownload size={18} />
    </ActionIcon>
  ) : (
    <Button
      size="xs"
      variant="light"
      color="inovareTeal"
      leftSection={<IconDownload size={16} />}
      onClick={onClick}
    >
      Instalar app
    </Button>
  )

  return (
    <>
      {trigger}
      <Modal
        opened={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Instalar o app na tela inicial"
        centered
        size="sm"
      >
        <Text size="sm" mb="sm">
          Para instalar o ConsoleAI como app no seu celular:
        </Text>
        <List size="sm" spacing="xs">
          <List.Item>
            <b>Android (Chrome):</b> toque no menu <b>⋮</b> e escolha{' '}
            <b>“Instalar app”</b> ou <b>“Adicionar à tela inicial”</b>.
          </List.Item>
          <List.Item>
            <b>iPhone (Safari):</b> toque em <b>Compartilhar</b> e escolha{' '}
            <b>“Adicionar à Tela de Início”</b>.
          </List.Item>
        </List>
        <Text size="xs" c="dimmed" mt="md">
          Depois de instalado, abra pelo ícone na tela inicial — a sessão fica
          ativa e as notificações chegam direto no celular.
        </Text>
      </Modal>
    </>
  )
}
